import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button as Button, F0TagStatus, F0Text, ProgressBarChart } from '@factorialco/f0-react'
import { TrainingsEntitiesContentBlockStatusEnum } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import ContentConsumer from 'modules/trainings/components/content/Consumer'
import { FormMembershipProvider } from 'modules/trainings/components/content/FormMembershipContext'
import { buildCollapsibleMenuItems } from 'modules/trainings/components/content/helpers/buildCollapsibleMenuItems'
import { ContentConsumerCallbacks } from 'modules/trainings/components/content/types'
import { Block } from 'modules/trainings/lib/content/Block'
import { ContentModule } from 'modules/trainings/lib/content/Module'
import { ModuleCollection } from 'modules/trainings/lib/content/ModuleCollection'
import { ConsumerCompletedState } from 'modules/trainings/pages/content/ConsumerCompletedState'

import { PreviewModeProvider } from './PreviewModeProvider'

interface ContentPreviewProps {
  collection: ModuleCollection
  onClose: () => void
}

const ContentPreviewInner = observer(({ collection, onClose }: ContentPreviewProps) => {
  const consumerRef = useRef<ContentConsumerCallbacks>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showEmptyState, setShowEmptyState] = useState(false)
  const [initialized, setInitialized] = useState(false)

  const originalSelectedUuid = useRef(collection.selectedItemUuid)
  const originalStatuses = useRef<Map<string, TrainingsEntitiesContentBlockStatusEnum>>(new Map())

  useEffect(() => {
    const nonModuleBlocks = collection.flatItems.filter(
      (item): item is Block => item instanceof Block
    )

    // Save original statuses
    nonModuleBlocks.forEach((item) => {
      originalStatuses.current.set(item.uuid, item.status)
    })

    // Mark all blocks before the current one as completed
    const currentSelectedUuid = originalSelectedUuid.current
    if (currentSelectedUuid) {
      const currentIndex = nonModuleBlocks.findIndex((item) => item.uuid === currentSelectedUuid)
      if (currentIndex > 0) {
        for (let i = 0; i < currentIndex; i++) {
          const block = nonModuleBlocks[i]
          if (block) {
            block.status = 'completed'
          }
        }
      }
    }

    setInitialized(true)
  }, [collection])

  useEffect(() => {
    if (!collection.selectedItem && collection.modules[0]) {
      collection.setSelected(collection.modules[0].uuid)
    }
  }, [collection])

  useEffect(() => {
    const savedUuid = originalSelectedUuid.current
    const savedStatuses = originalStatuses.current
    return () => {
      collection.setSelected(savedUuid)
      collection.flatItems.forEach((item) => {
        if (item instanceof Block) {
          const originalStatus = savedStatuses.get(item.uuid)
          if (originalStatus) {
            item.status = originalStatus
          }
        }
      })
    }
  }, [collection])
  const nonModuleItems = useMemo(
    () => collection.flatItems.filter((item): item is Block => !(item instanceof ContentModule)),
    [collection.flatItems]
  )

  const totalItems = nonModuleItems.length
  const currentPosition = useMemo(() => {
    if (showEmptyState) return totalItems
    if (!collection.selectedItem) return 0

    const blockIndex = nonModuleItems.findIndex(
      (item) => item.uuid === collection.selectedItem?.uuid
    )
    if (blockIndex >= 0) return blockIndex + 1

    // If current item is a module, find the last block before it
    const flatIndex = collection.flatItems.findIndex(
      (item) => item.uuid === collection.selectedItem?.uuid
    )
    if (flatIndex > 0) {
      for (let i = flatIndex - 1; i >= 0; i--) {
        const item = collection.flatItems[i]
        const blockPosition = nonModuleItems.findIndex((b) => b.uuid === item?.uuid)
        if (blockPosition >= 0) return blockPosition + 1
      }
    }

    return 0
  }, [showEmptyState, totalItems, collection.selectedItemUuid, nonModuleItems])

  const completedItems = initialized
    ? nonModuleItems.filter((item) => item.status === 'completed').length
    : 0
  const progressPercentage = Math.round((completedItems / totalItems) * 100)
  const isFirstModule = collection.selectedItem === collection.modules[0]

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const advanceToNext = useCallback(() => {
    const items = collection.flatItems
    const currentIndex = items.findIndex((item) => item.uuid === collection.selectedItem?.uuid)

    for (let i = currentIndex + 1; i < items.length; i++) {
      const item = items[i]
      if (item) {
        collection.setSelected(item.uuid)
        return
      }
    }

    collection.setSelected(undefined)
    setShowEmptyState(true)
  }, [collection])

  const handleNext = useCallback(async () => {
    if (collection.selectedItem instanceof ContentModule) {
      collection.next()
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    if (collection.selectedItem instanceof Block) {
      if (consumerRef.current && !(await consumerRef.current.handleNext())) return

      collection.selectedItem.status = 'completed'

      advanceToNext()
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [collection, advanceToNext])

  const handleRestart = useCallback(() => {
    collection.flatItems.forEach((item) => {
      if (item instanceof Block) {
        const originalStatus = originalStatuses.current.get(item.uuid)
        if (originalStatus) {
          item.status = originalStatus
        }
      }
    })
    const firstItem = collection.flatItems[0]
    if (firstItem) {
      collection.setSelected(firstItem.uuid)
      setShowEmptyState(false)
    }
  }, [collection])

  const menuItems = useMemo(
    () => buildCollapsibleMenuItems(collection),
    [collection, collection.selectedItem, completedItems]
  )

  const content = (
    <FormMembershipProvider memberships={[]}>
      <Box
        position="fixed"
        width="full"
        height="full"
        background="grey000White"
        style={{ zIndex: '999', top: '0', left: '0' }}
      >
        <Box
          position="fixed"
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          padding="s16"
          paddingX="s24"
          background="grey000White"
          height="s64"
          style={{ top: '0', left: '0', right: '0', zIndex: '11' }}
        >
          <Box flexDirection="row" alignItems="center" gap="s16">
            <Box width="s200" gap="s8" flexDirection="row" alignItems="center">
              <F0Text
                content={i18n.t('trainings.content.consumer.progress')}
                variant="description"
              />
              <Box width="full">
                <ProgressBarChart
                  value={completedItems}
                  max={totalItems}
                  label={`${progressPercentage}%`}
                  color="feedback-positive"
                />
              </Box>
            </Box>
          </Box>

          <Box flexDirection="row" alignItems="center" gap="s12">
            <F0TagStatus text={i18n.t('trainings.content.consumer.preview.title')} variant="info" />
            <F0Text content={`${currentPosition}/${totalItems}`} variant="description" />
            <F0Text content="|" variant="small" />
            <Button
              label={i18n.t('trainings.content.consumer.preview.exit')}
              variant="outline"
              onClick={onClose}
            />
          </Box>
        </Box>

        <Box
          ref={scrollContainerRef}
          flexDirection="column"
          height="full"
          flexGrow
          overflowY="auto"
          padding="s4"
          paddingTop="s64"
        >
          {showEmptyState ? (
            <Box alignItems="center" justifyContent="center" height="full">
              <ConsumerCompletedState
                isPreviewMode
                onClosePreview={onClose}
                onRestart={handleRestart}
              />
            </Box>
          ) : (
            <ContentConsumer
              selectedItem={collection.selectedItem}
              ref={consumerRef}
              showEmptyState={false}
              menuItems={menuItems}
              onBack={isFirstModule ? undefined : () => collection.back()}
              onNext={handleNext}
              onRestart={handleRestart}
            />
          )}
        </Box>
      </Box>
    </FormMembershipProvider>
  )

  if (typeof document === 'undefined') return content

  const mainElement = document.querySelector('main')
  return mainElement ? createPortal(content, mainElement) : content
})

export const ContentPreview = ({ collection, onClose }: ContentPreviewProps) => {
  return (
    <PreviewModeProvider>
      <ContentPreviewInner collection={collection} onClose={onClose} />
    </PreviewModeProvider>
  )
}
